import { useCallback, useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";



/**
 * Lightweight first-visit guided tour: dims the page, spotlights each target
 * element and shows a compact tooltip. No external dependency.
 */
export default function GuidedTour({ steps, labels, run, onClose }) {
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);

  const step = run ? steps[idx] : null;

  const measure = useCallback(() => {
    if (!step?.target) {
      setRect(null);
      return;
    }
    const el = document.querySelector(step.target);
    if (!el) {
      setRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [step]);

  useEffect(() => {
    if (!run) return;
    const el = step?.target ? document.querySelector(step.target) : null;
    if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
    const t = setTimeout(measure, 450);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, idx]);

  if (!run || !step) return null;
  const last = idx === steps.length - 1;

  let tipStyle;
  if (!rect) {
    tipStyle = { top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: Math.min(340, window.innerWidth - 32) };
  } else {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = Math.min(340, vw - 32);
    let top = rect.top + rect.height + 14;
    if (top + 190 > vh) top = Math.max(14, rect.top - 190 - 14);
    const left = Math.min(Math.max(16, rect.left + rect.width / 2 - w / 2), vw - w - 16);
    tipStyle = { top, left, width: w };
  }

  return (
    <div data-testid="oracle-tour-overlay" className="fixed inset-0 z-[90]" role="dialog" aria-modal="true" aria-label={labels.replay}>
      {rect ? (
        <div
          data-testid="oracle-tour-spotlight"
          className="pointer-events-none fixed rounded-2xl border-2 border-sky-400"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
            boxShadow: "0 0 0 9999px rgba(3, 20, 32, 0.55), 0 0 24px rgba(56, 189, 248, 0.6)",
          }}
        />
      ) : (
        <div className="absolute inset-0 bg-slate-900/55" />
      )}
      <div
        data-testid="oracle-tour-card"
        className="fixed z-[95] rounded-2xl border border-sky-200 bg-white/95 p-5 shadow-2xl shadow-sky-900/20 backdrop-blur-md"
        style={tipStyle}
      >
        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-sky-600">
          {idx + 1} / {steps.length}
        </p>
        <p data-testid="oracle-tour-title" className="mt-1 font-headline text-base font-extrabold text-ink">
          {step.t}
        </p>
        <p className="mt-2 text-sm leading-relaxed text-ink/70">{step.b}</p>
        <div className="mt-4 flex items-center justify-between">
          <button
            data-testid="oracle-tour-skip"
            onClick={onClose}
            className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink/45 transition-colors hover:text-ink"
          >
            {labels.skip}
          </button>
          <button
            data-testid="oracle-tour-next"
            onClick={() => (last ? onClose() : setIdx(idx + 1))}
            className="inline-flex items-center gap-1.5 rounded-full bg-teal px-5 py-2 font-headline text-xs font-bold text-white transition-colors duration-300 hover:bg-deep"
          >
            {last ? labels.done : labels.next}
            {!last && <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>
    </div>
  );
}
